import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, SlidersHorizontal } from "lucide-react";

const MODES = [["all", "All"], ["rent", "Rent"], ["buy", "Buy"], ["sale", "Sale"]];
const CATS = [["all", "All"], ["residential", "Residential"], ["commercial", "Commercial"], ["land", "Land / Plot"]];
const FURN = [["all", "Any"], ["fully-furnished", "Furnished"], ["semi-furnished", "Semi-Furnished"], ["unfurnished", "Unfurnished"], ["raw", "Raw / Bare Shell"]];
const PROP_TYPES = [["all", "All Types"], ["Office Space", "Office"], ["Co-working Space", "Co-working"], ["Virtual Office", "Virtual Office"], ["Desks", "Desks / Seats"], ["Shop", "Shop"], ["Warehouse", "Warehouse"], ["Apartment", "Apartment"], ["Independent House", "House"], ["Villa", "Villa"], ["Builder Floor", "Builder Floor"], ["PG / Room", "PG / Room"], ["Residential Plot", "Plot / Land"]];
const selVal = (v) => (v === "" ? "all" : v);

export default function Listings() {
  const [params, setParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    mode: params.get("mode") || "", city: params.get("city") || "",
    pin_code: params.get("pin_code") || "", property_type: params.get("property_type") || "",
    bhk: params.get("bhk") || "", furnishing: params.get("furnishing") || "", category: params.get("category") || "",
    max_price: params.get("max_price") || "", locality: params.get("locality") || "", min_area: params.get("min_area") || "",
  });

  const fetchProps = async (f) => {
    setLoading(true);
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    try {
      const res = await api.get(`/properties?${q.toString()}`);
      setProperties(res.data);
    } catch {
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchProps(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const apply = () => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && q.set(k, v));
    setParams(q);
  };

  const set = (k) => (v) => setFilters({ ...filters, [k]: v === "all" ? "" : v });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2">
        <SlidersHorizontal className="h-5 w-5 text-blue-600" />
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Property Listings</h1>
      </div>

      <div className="mt-6 rounded-2xl border bg-card p-4" data-testid="listings-filter-panel">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Select value={selVal(filters.mode)} onValueChange={set("mode")}>
            <SelectTrigger data-testid="filter-mode"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent>{MODES.map(([v, l]) => <SelectItem key={l} value={v} data-testid={`filter-mode-${v}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.city} onValueChange={set("city")}>
            <SelectTrigger data-testid="filter-city"><SelectValue placeholder="City" /></SelectTrigger>
            <SelectContent>{locations.map((l) => <SelectItem key={l.city} value={l.city}>{l.city}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.pin_code} onValueChange={set("pin_code")} disabled={!filters.city}>
            <SelectTrigger data-testid="filter-pin"><SelectValue placeholder="PIN Code" /></SelectTrigger>
            <SelectContent>
              {(locations.find((l) => l.city === filters.city)?.pincodes || []).map((p) => (
                <SelectItem key={p.pin} value={p.pin}>{p.locality} · {p.pin}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selVal(filters.category)} onValueChange={set("category")}>
            <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>{CATS.map(([v, l]) => <SelectItem key={l} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={selVal(filters.bhk)} onValueChange={set("bhk")}>
            <SelectTrigger data-testid="filter-bhk"><SelectValue placeholder="BHK" /></SelectTrigger>
            <SelectContent>{["1 BHK", "2 BHK", "3 BHK", "4 BHK"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={selVal(filters.property_type)} onValueChange={set("property_type")}>
            <SelectTrigger data-testid="filter-workspace-type"><SelectValue placeholder="Type / Workspace" /></SelectTrigger>
            <SelectContent>{PROP_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={selVal(filters.furnishing)} onValueChange={set("furnishing")}>
            <SelectTrigger data-testid="filter-furnishing"><SelectValue placeholder="Furnishing" /></SelectTrigger>
            <SelectContent>{FURN.map(([v, l]) => <SelectItem key={l} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="filter-min-area" type="number" placeholder="Min area sq.ft" value={filters.min_area} onChange={(e) => set("min_area")(e.target.value)} />
          <Input data-testid="filter-max-price" type="number" placeholder="Max price ₹" value={filters.max_price} onChange={(e) => set("max_price")(e.target.value)} />
          <Button data-testid="filter-apply-button" onClick={apply} className="gap-2"><Search className="h-4 w-4" /> Apply</Button>
        </div>
      </div>

      <p className="mt-6 text-sm text-muted-foreground" data-testid="listings-count">
        {loading ? "Searching..." : `${properties.length} properties found`}
      </p>
      <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="listings-grid">
        {properties.map((p) => <PropertyCard key={p.property_id} property={p} />)}
      </div>
      {!loading && properties.length === 0 && (
        <div className="mt-16 text-center text-muted-foreground" data-testid="no-results">
          No properties match your filters. Try widening the search.
        </div>
      )}
    </div>
  );
}
